// frontend/src/components/automation/ScreenshotGallery.jsx - Enhanced with scroll effects
import React, { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Eye, Download, Maximize2, ChevronLeft, ChevronRight, Grid, List } from 'lucide-react'
import ImageLightbox from '@components/common/ImageLightbox'

const ScreenshotGallery = ({ screenshots = [] }) => {
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [viewMode, setViewMode] = useState('grid') // 'grid' or 'carousel'
  const [showScrollButtons, setShowScrollButtons] = useState(false)
  const scrollContainerRef = useRef(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)
  
  console.log('Screenshots:', screenshots)

  // Check scroll position and update button states
  const checkScrollButtons = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current
      setCanScrollLeft(scrollLeft > 0)
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1)
      setShowScrollButtons(scrollWidth > clientWidth)
    }
  }

  // Update scroll buttons when screenshots change
  useEffect(() => {
    checkScrollButtons()
    
    // Add scroll listener
    const container = scrollContainerRef.current
    if (container) {
      container.addEventListener('scroll', checkScrollButtons)
      return () => container.removeEventListener('scroll', checkScrollButtons)
    }
  }, [screenshots])

  // Smooth scroll functions
  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      const scrollAmount = viewMode === 'carousel' ? 300 : 240
      scrollContainerRef.current.scrollBy({
        left: -scrollAmount,
        behavior: 'smooth'
      })
    }
  }

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      const scrollAmount = viewMode === 'carousel' ? 300 : 240
      scrollContainerRef.current.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      })
    }
  }

  // Scroll to specific screenshot
  const scrollToScreenshot = (index) => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current
      const itemWidth = viewMode === 'carousel' ? 280 : 200
      const scrollPosition = index * itemWidth
      
      container.scrollTo({
        left: scrollPosition,
        behavior: 'smooth'
      })
    }
  }

  const openLightbox = (index) => {
    setCurrentImageIndex(index)
    setLightboxOpen(true)
  }

  const downloadImage = (screenshot) => {
    const link = document.createElement('a')
    link.href = `data:image/png;base64,${screenshot.base64 || screenshot.full_base64}`
    link.download = `screenshot-step-${screenshot.step_number}.png`
    link.click()
  }

  if (screenshots.length === 0) {
    return (
      <motion.div 
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center text-gray-500 py-12"
      >
        <motion.div
          animate={{ y: [0, -5, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="w-16 h-16 mx-auto mb-4 bg-gradient-to-r from-gray-100 to-slate-100 rounded-2xl flex items-center justify-center"
        >
          <Eye className="h-8 w-8 text-gray-400" />
        </motion.div>
        <p className="font-semibold text-lg mb-1">No screenshots yet</p>
        <p className="text-sm text-gray-400">Screenshots will appear here as tests run</p>
      </motion.div>
    )
  }

  return (
    <>
      {/* Gallery Header with Controls */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-600 font-medium">
            {screenshots.length} screenshot{screenshots.length !== 1 ? 's' : ''}
          </span>
          
          {/* View Mode Toggle */}
          <div className="flex items-center bg-gray-100 rounded-lg p-1">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setViewMode('grid')}
              className={`p-1.5 rounded-md transition-all duration-200 ${
                viewMode === 'grid' 
                  ? 'bg-white text-blue-600 shadow-sm' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              title="Grid view"
            >
              <Grid className="h-4 w-4" />
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setViewMode('carousel')}
              className={`p-1.5 rounded-md transition-all duration-200 ${
                viewMode === 'carousel' 
                  ? 'bg-white text-blue-600 shadow-sm' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              title="Carousel view"
            >
              <List className="h-4 w-4" />
            </motion.button>
          </div>
        </div>

        {/* Scroll Navigation Buttons */}
        {showScrollButtons && (
          <div className="flex items-center space-x-1">
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={scrollLeft}
              disabled={!canScrollLeft}
              className={`p-2 rounded-lg border transition-all duration-200 ${
                canScrollLeft 
                  ? 'bg-white hover:bg-gray-50 text-gray-700 border-gray-300 hover:border-gray-400' 
                  : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
              }`}
              title="Scroll left"
            >
              <ChevronLeft className="h-4 w-4" />
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={scrollRight}
              disabled={!canScrollRight}
              className={`p-2 rounded-lg border transition-all duration-200 ${
                canScrollRight 
                  ? 'bg-white hover:bg-gray-50 text-gray-700 border-gray-300 hover:border-gray-400' 
                  : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
              }`}
              title="Scroll right"
            >
              <ChevronRight className="h-4 w-4" />
            </motion.button>
          </div>
        )}
      </div>

      {/* Screenshots Container */}
      <div className="relative">
        <div 
          ref={scrollContainerRef}
          className={`
            ${viewMode === 'grid' 
              ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3' 
              : 'flex space-x-4 overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100'
            }
            ${viewMode === 'carousel' ? 'pb-2' : ''}
          `}
          style={{ 
            scrollbarWidth: 'thin',
            scrollSnapType: viewMode === 'carousel' ? 'x mandatory' : 'none'
          }}
        >
          {screenshots.map((screenshot, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ scale: 1.02, y: -2 }}
              className={`
                relative group cursor-pointer flex-shrink-0
                ${viewMode === 'carousel' ? 'w-72' : ''}
              `}
              style={{ scrollSnapAlign: viewMode === 'carousel' ? 'start' : 'none' }}
              onClick={() => openLightbox(index)}
            >
              <div className="relative overflow-hidden rounded-xl border-2 border-gray-200 group-hover:border-blue-300 transition-all duration-300 shadow-sm group-hover:shadow-lg">
                <img
                  src={`data:image/png;base64,${screenshot.base64 || screenshot.full_base64}`}
                  alt={`Step ${screenshot.step_number}`}
                  className={`
                    w-full object-cover transition-transform duration-300 group-hover:scale-105
                    ${viewMode === 'carousel' ? 'h-48' : 'h-32'}
                  `}
                />
                
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300" />
                
                {/* Hover Actions */}
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300">
                  <motion.div
                    initial={{ scale: 0.8 }}
                    whileHover={{ scale: 1.1 }}
                    className="bg-white/90 backdrop-blur-sm rounded-full p-3 shadow-lg"
                  >
                    <Maximize2 className="h-5 w-5 text-gray-700" />
                  </motion.div>
                </div>
                
                {/* Step Number Badge */}
                <div className="absolute top-3 left-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
                  Step {screenshot.step_number}
                </div>
                
                {/* Quick Download */}
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={(e) => {
                    e.stopPropagation()
                    downloadImage(screenshot)
                  }}
                  className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm hover:bg-white text-gray-700 p-2 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-all duration-300"
                  title="Download screenshot"
                >
                  <Download className="h-4 w-4" />
                </motion.button>
                
                {/* Action Label */}
                {screenshot.action && (
                  <div className="absolute bottom-3 left-3 right-3">
                    <div className="bg-black/70 backdrop-blur-sm text-white text-xs px-3 py-2 rounded-lg truncate">
                      <span className="font-medium">{screenshot.action}</span>
                    </div>
                  </div>
                )}

                {/* Progress Indicator for Carousel */}
                {viewMode === 'carousel' && (
                  <div className="absolute bottom-3 right-3">
                    <div className="bg-black/60 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-md">
                      {index + 1}/{screenshots.length}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Gradient Fade Effects for Carousel */}
        {viewMode === 'carousel' && showScrollButtons && (
          <>
            {canScrollLeft && (
              <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-white to-transparent pointer-events-none z-10" />
            )}
            {canScrollRight && (
              <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-white to-transparent pointer-events-none z-10" />
            )}
          </>
        )}
      </div>

      {/* Screenshot Navigation Dots for Carousel */}
      {viewMode === 'carousel' && screenshots.length > 1 && (
        <div className="flex justify-center space-x-2 mt-4">
          {screenshots.map((_, index) => (
            <motion.button
              key={index}
              whileHover={{ scale: 1.2 }}
              whileTap={{ scale: 0.8 }}
              onClick={() => {
                scrollToScreenshot(index)
                setCurrentImageIndex(index)
              }}
              className={`w-2 h-2 rounded-full transition-all duration-200 ${
                index === currentImageIndex 
                  ? 'bg-blue-600 w-6' 
                  : 'bg-gray-300 hover:bg-gray-400'
              }`}
            />
          ))}
        </div>
      )}

      {/* Quick Actions Bar */}
      {screenshots.length > 0 && (
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200"
        >
          <div className="flex items-center justify-between text-xs text-gray-600">
            <span>
              Click any screenshot to view in full size
            </span>
            <div className="flex items-center space-x-2">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => openLightbox(0)}
                className="text-blue-600 hover:text-blue-700 font-medium"
              >
                View Gallery
              </motion.button>
              <span className="text-gray-400">•</span>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  // Download all screenshots as zip (placeholder)
                  screenshots.forEach((screenshot, index) => {
                    setTimeout(() => downloadImage(screenshot), index * 100)
                  })
                }}
                className="text-green-600 hover:text-green-700 font-medium"
              >
                Download All
              </motion.button>
            </div>
          </div>
        </motion.div>
      )}

      {/* Standalone Lightbox */}
      <ImageLightbox
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        images={screenshots}
        currentIndex={currentImageIndex}
        onIndexChange={setCurrentImageIndex}
        showNavigation={true}
        showControls={true}
        showDownload={true}
      />
    </>
  )
}

export default ScreenshotGallery